import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PageSkeleton } from '../../components/UI/Skeleton/Skeleton';
import { useNavigate } from 'react-router-dom';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { useGetDashboardDataQuery } from '../../redux/apis/Dashboard';
import { getUserName } from '../../utils/handler';

import UpcomingCelebrationsTabs from './components/UpcomingCelebrationsTabs';
import UpcomingHolidays from './components/UpcomingHolidays';
import DashboardLeavesTabs from './components/DashboardLeavesTabs';
import AttendanceMetricCardsGrid from './components/AttendanceMetricCardsGrid';
import EmployeeDayTimelineCard from './components/EmployeeDayTimelineCard';
// import EmployeeTimeTotalsCards from './components/EmployeeTimeTotalsCards';

import { LEAVE_STATUS, ROLES } from '../../utils/constant/global';
import { useEditLeaveRequestMutation } from '../../redux/apis/LeaveRequest';
import { notification } from '../../utils/notification';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
// import LopAndOvertimeCard from '../../components/Employees/LopAndOvertimeCard';
import CelebrationModal from './components/CelebrationDialog';
import OrgChartComponent from '../App/OrgChart';
import OfficeStatusCards from './components/OfficeStatusCards';
// import OnBreakCard from './components/OnBreakCard';
import AttendanceViolationWidget from './components/AttendanceViolationWidget';
import MyWarningsWidget from './components/MyWarningsWidget';

import { selectApiLocality, selectDisplayTimezone } from '../../redux/utils/apiLocality';
import { formatTimestampInTimezone } from '../../utils/timedoctorAttendanceHelpers';
import { getDashboardHeroTagline } from './utils/dashboardHeroTagline';

import './Dashboard.scss';

// const { LOADING } = GLOBAL;
const { PENDING, ACCEPTED, REJECTED } = LEAVE_STATUS;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: isLoggedInData } = useIsLoggedInQuery();
  const { data, isLoading, isFetching, refetch } = useGetDashboardDataQuery(
    { isOurUser: true },
    { refetchOnMountOrArgChange: true }
  );
  const [editLeaveRequest] = useEditLeaveRequestMutation();

  // eslint-disable-next-line no-unused-vars
  const [tabClick, setTabClick] = useState('list');
  const [showCelebration, setShowCelebration] = useState(true);

  const roleName = isLoggedInData?.data?.hrxRole?.roleName;
  const isHrOrAdmin =
    roleName === ROLES.ADMIN || roleName === ROLES.HR || roleName === ROLES.ACCOUNTANT;
  const canReviewLeave = roleName === ROLES.ADMIN || roleName === ROLES.HR;
  const locality = useSelector(selectApiLocality);
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';
  const showAttendanceViolations = !locality.includes('AU');
  const dashboardLayout = isHrOrAdmin
    ? showAttendanceViolations
      ? 'admin'
      : 'admin-au'
    : showAttendanceViolations
      ? 'member'
      : 'member-au';
  const d = data?.data || {};

  const activeEmployeeCount = useMemo(() => {
    const candidates = [
      // d?.totalActiveEmployees,
      // d?.activeEmployeeCount,
      // d?.totalActiveEmployee,
      // d?.totalEmployeesCount,
      // d?.totalEmployees,
      // d?.employeesCount,
      // d?.totalEmployee,
      // d?.activeEmployeesCount,
      // d?.activeEmployees,
      d?.totalActiveUsersCount
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const n = Number(candidates[i]);
      if (Number.isFinite(n) && n >= 0) return Math.floor(n);
    }
    return 0;
  }, [d]);

  const heroMetric = useMemo(
    () => ({
      key: 'active-employees',
      label: 'Total employees',
      value: activeEmployeeCount,
      iconClass: 'icofont-users-alt-2'
    }),
    [activeEmployeeCount]
  );

  /*   const celebrationsHasData = useMemo(() => {
    const hasBirthdays = (d?.todaysDOBs || []).length > 0;
    const hasAnniversaries = (d?.todaysWorkAnniversary || []).length > 0;
    const hasHiring = (d?.last7DaysHiring || []).length > 0;
    return hasBirthdays || hasAnniversaries || hasHiring;
  }, [d]); */

  /*  const onBreakList = useMemo(() => {
    return d?.onBreakUsers || d?.breakUsers || d?.breakInUsers || d?.employeesOnBreak || [];
  }, [d]); */

  const workFromHomeList = useMemo(() => {
    const raw = d?.todayWorkFromHomeUsers;
    if (!Array.isArray(raw)) return [];
    return raw
      .flatMap((g) => (Array.isArray(g?.workFromHomeUsers) ? g.workFromHomeUsers : []))
      .filter((x) => x && x.user);
  }, [d]);

  const attendanceCards = useMemo(() => {
    const fmtTime = (ms) => {
      const n = Number(ms);
      if (!Number.isFinite(n) || n <= 0) return '';
      return formatTimestampInTimezone(n, timeZone, true);
    };

    /* const fmtDate = (ms) => {
      const n = Number(ms);
      if (!Number.isFinite(n) || n <= 0) return '';
      try {
        return new Date(n).toLocaleDateString('en-GB', {
          timeZone,
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return new Date(n).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }; */

    const fmtHours = (milliseconds) => {
      const n = Number(milliseconds);
      if (!Number.isFinite(n) || n <= 0) return '';
      const totalSeconds = Math.floor(n / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      const parts = [];
      if (h) parts.push(`${h}h`);
      if (m) parts.push(`${m}m`);
      if (s || !parts.length) parts.push(`${s}s`);
      return parts.join(' ');
    };

    const buildAvatar = ({ src, name, designation, details = [], extra = {} }) => {
      return {
        src: src || '',
        tooltip: {
          name: name || 'Unknown',
          designation: designation || '',
          details: (details || []).filter((x) => x && x.label && x.value)
        },
        ...extra
      };
    };

    const peopleFrom = (list = [], pick) =>
      (list || [])
        .map(pick)
        .filter((x) => x && (x.src || x?.tooltip?.name))
        .slice(0, 200);

    const toCard = ({ id, label, title, value, percent, theme, iconClass, people }) => {
      const all = people || [];
      const preview = all.slice(0, 3);
      const extra = all.length > 3 ? `+${all.length - 3}` : '';
      return {
        id,
        label: label || title,
        title: title || label,
        value: Number(value || 0),
        percent: Math.round(Number(percent || 0) * 100) / 100,
        extraCount: extra,
        theme,
        iconClass,
        avatars: preview.length ? preview : [],
        people: all
      };
    };

    return [
      {
        id: 'employees-check-in',
        tabs: [
          toCard({
            id: 'employees-in',
            label: 'Employees In',
            title: 'Employees In',
            value: d?.todayCheckInUsersCount ?? (d?.todayCheckInUsers || []).length,
            percent: d?.todayCheckInUsersPercentage,
            theme: 'green',
            iconClass: 'icofont-business-man',
            people: peopleFrom(d?.todayCheckInUsers, (x) =>
              buildAvatar({
                src: x?.user?.profilePicture,
                name: x?.user?.name,
                designation: x?.user?.designation,
                details: [
                  {
                    label: 'Check-in',
                    value: x?.firstCheckInTime ? fmtTime(x.firstCheckInTime) : ''
                  },
                  {
                    label: 'Check-out',
                    value: x?.lastCheckOutTime ? fmtTime(x.lastCheckOutTime) : ''
                  }
                ]
              })
            )
          }),
          toCard({
            id: 'not-qr-check-in',
            label: 'Not Check In',
            title: 'Not Check In QR Code',
            value: d?.todayNotQrCheckInUsersCount ?? (d?.todayNotQrCheckInUsers || []).length,
            percent:
              d?.todayNotQrCheckInUsersPercentage ??
              (d?.totalActiveUsersCount > 0
                ? (Number(
                    d?.todayNotQrCheckInUsersCount ?? (d?.todayNotQrCheckInUsers || []).length
                  ) /
                    Number(d.totalActiveUsersCount)) *
                  100
                : 0),
            theme: 'purple',
            iconClass: 'icofont-qr-code',
            people: peopleFrom(d?.todayNotQrCheckInUsers, (x) =>
              buildAvatar({
                src: x?.user?.profilePicture,
                name: x?.user?.name,
                designation: x?.user?.designation,
                details: []
              })
            )
          })
        ]
      },
      (() => {
        const todayLeaves = Array.isArray(d?.todayLeaveUsers) ? d.todayLeaveUsers : [];
        const totalActive = Number(d?.totalActiveUsersCount) || 0;
        const sharePct = (count) => (totalActive > 0 ? (Number(count) / totalActive) * 100 : 0);
        const leaveDayValue = (x) =>
          x?.leaveDayType
            ? x.leaveDayType
            : x?.isHalfDay === true
              ? 'Half Day'
              : x?.isHalfDay === false
                ? 'Full Day'
                : '';
        const mapTodayLeavePeople = (list, reviewActions = []) =>
          peopleFrom(list, (x) =>
            buildAvatar({
              src: x?.userId?.profilePicture,
              name: x?.userId?.name,
              designation: x?.userId?.designations?.name,
              details: [
                { label: 'Type', value: x?.leaveType || '' },
                { label: 'Day', value: leaveDayValue(x) }
              ],
              extra: {
                leaveId: x?._id || '',
                leaveStatus: x?.leaveStatus || '',
                reviewActions
              }
            })
          );
        const pendingToday = todayLeaves.filter((x) => (x?.leaveStatus || PENDING) === PENDING);
        const approvedToday = todayLeaves.filter((x) => x?.leaveStatus === ACCEPTED);
        const rejectedToday = todayLeaves.filter((x) => x?.leaveStatus === REJECTED);

        return {
          id: 'leaves-today',
          title: 'Leaves (Today)',
          tablistLabel: "Today's leave status",
          showTabCounts: true,
          tabs: [
            toCard({
              id: 'leaves-today-pending',
              label: 'Pending',
              title: 'Pending leaves (Today)',
              value: pendingToday.length,
              percent: sharePct(pendingToday.length),
              theme: 'yellow',
              iconClass: 'icofont-clock-time',
              people: mapTodayLeavePeople(pendingToday, canReviewLeave ? ['approve', 'reject'] : [])
            }),
            toCard({
              id: 'leaves-today-approved',
              label: 'Approved',
              title: 'Approved leaves (Today)',
              value: approvedToday.length,
              percent: sharePct(approvedToday.length),
              theme: 'green',
              iconClass: 'icofont-check-circled',
              people: mapTodayLeavePeople(approvedToday, canReviewLeave ? ['reject'] : [])
            }),
            toCard({
              id: 'leaves-today-rejected',
              label: 'Rejected',
              title: 'Rejected leaves (Today)',
              value: rejectedToday.length,
              percent: sharePct(rejectedToday.length),
              theme: 'red',
              iconClass: 'icofont-close-circled',
              people: mapTodayLeavePeople(rejectedToday, canReviewLeave ? ['approve'] : [])
            })
          ]
        };
      })(),
      toCard({
        id: 'were-late',
        title: 'Late arrival',
        value: d?.todayLateComingUsersCount ?? (d?.todayLateComingUsers || []).length,
        percent: d?.todayLateComingUsersPercentage,
        theme: 'yellow',
        iconClass: 'icofont-runner',
        people: peopleFrom(d?.todayLateComingUsers, (x) =>
          buildAvatar({
            src: x?.user?.profilePicture,
            name: x?.user?.name,
            designation: x?.user?.designation,
            details: [
              { label: 'Check-in', value: x?.firstCheckInTime ? fmtTime(x.firstCheckInTime) : '' },
              ...(x?.lateArrivalApplied ? [{ label: 'Late arrival', value: 'Applied' }] : [])
            ]
          })
        )
      }),
      toCard({
        id: 'left-early',
        title: 'Left early',
        value: d?.leftEarlyUsersCount ?? (d?.leftEarlyUsers || []).length,
        percent: d?.leftEarlyUsersPercentage,
        theme: 'blue',
        iconClass: 'icofont-exit',
        people: peopleFrom(d?.leftEarlyUsers, (x) =>
          buildAvatar({
            src: x?.user?.profilePicture,
            name: x?.user?.name,
            designation: x?.user?.designation,
            details: [
              { label: 'Check-out', value: x?.lastCheckOutTime ? fmtTime(x.lastCheckOutTime) : '' },
              {
                label: 'Worked',
                value: x?.totalWorkedMilliseconds ? fmtHours(x.totalWorkedMilliseconds) : ''
              },
              ...(x?.earlyExitApplied ? [{ label: 'Early exit', value: 'Applied' }] : [])
            ]
          })
        )
      })
      // toCard({
      //   id: 'wfh-today',
      //   title: 'Work from home',
      //   value: d?.todayWorkFromHomeUsersCount ?? workFromHomeList.length,
      //   percent: d?.todayWorkFromHomeUsersPercentage,
      //   theme: 'purple',
      //   iconClass: 'icofont-home',
      //   people: peopleFrom(workFromHomeList, (x) =>
      //     buildAvatar({
      //       src: x?.user?.profilePicture,
      //       name: x?.user?.name,
      //       designation: x?.user?.designation,
      //       details: [
      //         {
      //           label: 'From',
      //           value: x?.startDate ? fmtDate(x.startDate) : ''
      //         },
      //         {
      //           label: 'To',
      //           value: x?.endDate ? fmtDate(x.endDate) : ''
      //         }
      //       ]
      //     })
      //   )
      // })
    ];
  }, [d, workFromHomeList, timeZone, canReviewLeave]);

  const handleLeaveReview = useCallback(
    async ({ leaveId, status, reason }) => {
      if (!canReviewLeave || !leaveId) return false;
      try {
        const res = await editLeaveRequest({
          data: { leaveStatus: status, leaveReason: reason },
          id: leaveId
        });
        if (res.data?.success) {
          notification(res?.data?.statusMessage, TOAST_SUCCESS);
          refetch();
          return true;
        }
        notification(
          res?.error?.data?.statusMessage || res?.data?.statusMessage || 'Unable to update leave',
          TOAST_ERROR
        );
        return false;
      } catch (error) {
        notification(error?.message || 'Unable to update leave', TOAST_ERROR);
        return false;
      }
    },
    [canReviewLeave, editLeaveRequest, refetch]
  );

  if (!data && isLoading) {
    return (
      <div className="dashboard-page dashboard-page--loading">
        <PageSkeleton cards={4} rows={6} />
      </div>
    );
  }

  const user = isLoggedInData?.data;
  const displayName = getUserName(user) || 'there';
  const firstName = displayName.trim().split(/\s+/)[0] || 'there';
  let todayLabel;
  try {
    todayLabel = new Date().toLocaleDateString('en-US', {
      timeZone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    todayLabel = new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  const leavesWidget = isHrOrAdmin ? (
    <div className="dashboard-widget dashboard-widget--leaves">
      <DashboardLeavesTabs
        requestList={d?.upcoming7DaysLeaveRequest || []}
        approvedList={d?.upcoming7DaysLeaves || []}
      />
    </div>
  ) : null;

  return (
    <>
      <div className="dashboard-page">
        {tabClick === 'list' && (
          <>
            <header className="dashboard-hero">
              <div className="dashboard-hero__intro">
                <div className="dashboard-hero__person">
                  <div className="dashboard-hero__copy">
                    <div className="dashboard-hero__meta">
                      <p className="dashboard-hero__date">{todayLabel}</p>
                    </div>
                    <h1 className="dashboard-hero__title">
                      Hello, {firstName}!{' '}
                      <span className="dashboard-hero__wave" role="img" aria-label="Waving hand">
                        👋
                      </span>
                    </h1>
                    <p className="dashboard-hero__tagline">{getDashboardHeroTagline(timeZone)}</p>
                  </div>
                </div>
              </div>
              <div
                className="dashboard-hero__metrics dashboard-hero__metrics--solo"
                role="list"
                aria-label="Workforce summary"
              >
                <article
                  className="dashboard-hero-metric dashboard-hero-metric--solo"
                  role="listitem"
                >
                  <div className="dashboard-hero-metric__top">
                    <span className="dashboard-hero-metric__icon-wrap" aria-hidden>
                      <i className={`icofont ${heroMetric.iconClass}`} />
                    </span>
                  </div>
                  <p className="dashboard-hero-metric__value">
                    {Number(heroMetric.value).toLocaleString()}
                  </p>
                  <p className="dashboard-hero-metric__label">{heroMetric.label}</p>
                </article>
              </div>
            </header>

            <div className={`dashboard-grid dashboard-grid--${dashboardLayout}`}>
              <div className="dashboard-grid__col dashboard-grid__col--primary">
                {!isHrOrAdmin ? (
                  <div className="dashboard-widget dashboard-widget--timeline">
                    <EmployeeDayTimelineCard roleName={roleName} />
                  </div>
                ) : null}

                <div className="dashboard-widget dashboard-widget--celebrations">
                  <UpcomingCelebrationsTabs
                    birthdayList={data?.data?.todaysDOBs || []}
                    workAnniversaryList={data?.data?.todaysWorkAnniversary || []}
                    latestHiringList={data?.data?.last7DaysHiring || []}
                  />
                </div>

                {dashboardLayout === 'admin' ? (
                  <div className="dashboard-widget dashboard-widget--wfh">
                    <OfficeStatusCards
                      leaveList={data?.data?.todayLeaveUsers || []}
                      workFromHomeList={workFromHomeList}
                      isHrOrAdmin={isHrOrAdmin}
                      showWfh
                      showTodayLeave={false}
                    />
                  </div>
                ) : null}

                {isHrOrAdmin && showAttendanceViolations ? (
                  <div className="dashboard-widget dashboard-widget--violations">
                    <AttendanceViolationWidget
                      summary={d?.attendanceViolationSummary}
                      onRefresh={refetch}
                      loading={isLoading || isFetching}
                    />
                  </div>
                ) : null}

                {dashboardLayout === 'admin-au' ? leavesWidget : null}
              </div>

              <div className="dashboard-grid__col dashboard-grid__col--secondary">
                <div className="dashboard-widget dashboard-widget--rail">
                  <UpcomingHolidays
                    holidays={d?.upcomingHolidays || []}
                    onViewAll={() => navigate('/holidays')}
                  />
                  {isHrOrAdmin ? (
                    <AttendanceMetricCardsGrid
                      items={attendanceCards}
                      onLeaveReview={canReviewLeave ? handleLeaveReview : undefined}
                    />
                  ) : null}
                </div>

                {!isHrOrAdmin || dashboardLayout === 'admin-au' ? (
                  <div className="dashboard-widget dashboard-widget--wfh">
                    <OfficeStatusCards
                      leaveList={data?.data?.todayLeaveUsers || []}
                      workFromHomeList={workFromHomeList}
                      isHrOrAdmin={isHrOrAdmin}
                      showWfh
                      showTodayLeave={!isHrOrAdmin}
                    />
                  </div>
                ) : null}

                {!isHrOrAdmin && showAttendanceViolations ? (
                  <div className="dashboard-widget dashboard-widget--my-warnings">
                    <MyWarningsWidget />
                  </div>
                ) : null}

                {dashboardLayout === 'admin' ? leavesWidget : null}
              </div>
            </div>
          </>
        )}

        {tabClick === 'Organization-Chart' && (
          <div className="dashboard-org-surface">
            <OrgChartComponent />
          </div>
        )}
      </div>

      <CelebrationModal
        show={showCelebration}
        onClose={() => setShowCelebration(false)}
        todaysDOBs={data?.data?.todaysDOBs || []}
        todaysWorkAnniversary={data?.data?.todaysWorkAnniversary || []}
      />
    </>
  );
};

export default Dashboard;
